import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { VendorDTO } from '../types/Vendor';
import { formatTaxId, unformatTaxId } from '../utils/formatting';
import { tin } from '../constants/regex';
import CustomInput from '../controls/CustomInput';

const VendorFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [formData, setFormData] = useState<VendorDTO>({
    id: 0,
    vendorName: '',
    vendorId: '',
    nameOnCheck: '',
    accountNumber: '',
    taxId: '',
    is1099: false,
    primaryEmail: '',
    phone: '',
    paymentInfoEmail: '',
    addressLine1: '',
    addressLine2: '',
    addressCity: '',
    addressState: '',
    addressZipCode: '',
    addressCountry: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
  const loadVendor = async (vendorId: number) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/vendor/getvendor?id=${vendorId}`);
      const vendor = response.data.vendor;

      setFormData({
        ...vendor,
        taxId: formatTaxId(vendor.taxId)
      });
    } catch (error) {
      console.error('Error loading vendor:', error);
    } finally {
      setIsLoading(false);
    }
  };

    const vendorId = new URLSearchParams(location.search).get('vendorId');
    if (vendorId) {
      setIsEditing(true);
      loadVendor(parseInt(vendorId));
    }
  }, [location.search, api]);

  const handleFieldChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyId) return;

    try {
      setIsLoading(true);

      const submitData = {
        ...formData,
        taxId: unformatTaxId(formData.taxId)
      };

      if (isEditing) {
        await api.put(`/api/vendor/updatevendor?familyId=${familyId}`, submitData);
      } else {
        await api.post(`/api/vendor/createvendor?familyId=${familyId}`, submitData);
      }

      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving vendor:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Vendor' : 'Add New Vendor'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update vendor information' : 'Enter vendor information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CustomInput
                  label="Vendor Name"
                  name="vendorName"
                  value={formData.vendorName}
                  onChange={handleFieldChange('vendorName')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Vendor ID"
                  name="vendorId"
                  value={formData.vendorId}
                  onChange={handleFieldChange('vendorId')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Name on Check"
                  name="nameOnCheck"
                  value={formData.nameOnCheck}
                  onChange={handleFieldChange('nameOnCheck')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Account Number"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleFieldChange('accountNumber')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Tax ID"
                  name="taxId"
                  value={formData.taxId}
                  onChange={(val) => setFormData(prev => ({ ...prev, taxId: formatTaxId(val) }))}
                  maxLength={10}
                  pattern={tin.source}
                  tooltip="Format: XX-XXXXXXX"
                  required
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is1099"
                  checked={formData.is1099}
                  onChange={handleInputChange}
                  className="h-4 w-4 custom-checkbox"
                />
                <label className="ml-2 block text-sm font-medium text-gray-700">
                  1099 Vendor
                </label>
              </div>
            </div>
          </div>

          {/* Contact Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CustomInput
                  label="Primary Email"
                  name="primaryEmail"
                  type="email"
                  value={formData.primaryEmail}
                  onChange={handleFieldChange('primaryEmail')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleFieldChange('phone')}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <CustomInput
                  label="Payment Info Email"
                  name="paymentInfoEmail"
                  type="email"
                  value={formData.paymentInfoEmail}
                  onChange={handleFieldChange('paymentInfoEmail')}
                  required
                />
              </div>
            </div>
          </div>

          {/* Address Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <CustomInput
                  label="Address Line 1"
                  name="addressLine1"
                  value={formData.addressLine1 || ''}
                  onChange={handleFieldChange('addressLine1')}
                />
              </div>
              <div className="md:col-span-2">
                <CustomInput
                  label="Address Line 2"
                  name="addressLine2"
                  value={formData.addressLine2 || ''}
                  onChange={handleFieldChange('addressLine2')}
                />
              </div>
              <div>
                <CustomInput
                  label="City"
                  name="addressCity"
                  value={formData.addressCity || ''}
                  onChange={handleFieldChange('addressCity')}
                />
              </div>
              <div>
                <CustomInput
                  label="State"
                  name="addressState"
                  value={formData.addressState || ''}
                  onChange={handleFieldChange('addressState')}
                />
              </div>
              <div>
                <CustomInput
                  label="Zip Code"
                  name="addressZipCode"
                  value={formData.addressZipCode || ''}
                  onChange={handleFieldChange('addressZipCode')}
                />
              </div>
              <div>
                <CustomInput
                  label="Country"
                  name="addressCountry"
                  value={formData.addressCountry || ''}
                  onChange={handleFieldChange('addressCountry')}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Vendor' : 'Add Vendor')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default VendorFormPage;
